import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiChevronLeft, FiChevronRight, FiCamera } = FiIcons;

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);
  
  const galleryImages = [
    {
      src: 'https://media.publit.io/file/BigCitySupreme/tn-IMG-2314.jpg',
      alt: 'Big City Supreme performing live',
      category: 'Live Shows'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/IMG-1145-scaled-e1635349534439.jpg',
      alt: 'Band portrait on stage',
      category: 'Live Shows'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/0002404222-10.jpg',
      alt: 'Dallas on guitar',
      category: 'Band Members'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/556315843-24558904460433122-6684647581221885002-n.jpg',
      alt: 'Full band performance',
      category: 'Live Shows'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/554830198-24558397640483804-4086204311096154491-n.jpg',
      alt: 'Band in action',
      category: 'Live Shows'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/555408925-24543395705317331-2545825519502908711-n.jpg',
      alt: 'James on bass',
      category: 'Band Members'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/555520961-24546835431640025-2357011044751545291-n.jpg',
      alt: 'Denver on guitar',
      category: 'Band Members'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/556235113-24546835561640012-1087880150918556637-n.jpg',
      alt: 'Colin on drums',
      category: 'Band Members'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/tn-IMG-2202.jpg',
      alt: 'Recording session',
      category: 'Studio'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/tn-IMG-2129.jpg',
      alt: 'Band backstage',
      category: 'Behind the Scenes'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/Screenshot-2025-08-22-220250.jpg',
      alt: 'Live performance moment',
      category: 'Live Shows'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/tn-IMG-2084.jpg',
      alt: 'Band photo shoot',
      category: 'Behind the Scenes'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/tn-IMG-2128.jpg',
      alt: 'Studio recording',
      category: 'Studio'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/tn-IMG-2211.jpg',
      alt: 'Crowd going wild',
      category: 'Live Shows'
    },
    {
      src: 'https://media.publit.io/file/BigCitySupreme/tn-IMG-2342.jpg',
      alt: 'Band posing together',
      category: 'Band Members'
    }
  ];

  const categories = ['All', 'Live Shows', 'Band Members', 'Studio', 'Behind the Scenes'];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredImages = activeCategory === 'All' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeCategory);

  const openLightbox = (index) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateLightbox = (direction) => {
    if (selectedImage === null) return;
    
    const newIndex = direction === 'next' 
      ? (selectedImage + 1) % filteredImages.length
      : (selectedImage - 1 + filteredImages.length) % filteredImages.length;
    
    setSelectedImage(newIndex);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            className="text-5xl md:text-7xl font-bold bebas-neune gradient-text mb-6"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            GALLERY
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            A visual journey through the Supreme universe. 
            Warning: May contain traces of rock stardom and questionable fashion choices.
          </motion.p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-900 border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            layout
          >
            {filteredImages.map((image, index) => (
              <motion.div
                key={`${activeCategory}-${index}`}
                className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-800"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ scale: 1.05 }}
                onClick={() => openLightbox(index)}
                layout
              >
                <div className="aspect-square">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <SafeIcon icon={FiCamera} className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                  <p className="text-white text-sm font-medium">{image.alt}</p>
                  <p className="text-gray-300 text-xs">{image.category}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {filteredImages.length === 0 && (
            <motion.div 
              className="text-center py-20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <p className="text-gray-400 text-xl">No images found in this category.</p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage !== null && (
        <motion.div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeLightbox}
        >
          <div className="relative max-w-4xl max-h-full">
            <img
              src={filteredImages[selectedImage].src}
              alt={filteredImages[selectedImage].alt}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
            
            {/* Navigation buttons */}
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} className="h-6 w-6" />
            </button>
            
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
            >
              <SafeIcon icon={FiChevronRight} className="h-6 w-6" />
            </button>
            
            {/* Image info */}
            <div className="absolute bottom-4 left-4 right-4 bg-black/70 rounded-lg p-4">
              <h3 className="text-white font-semibold">{filteredImages[selectedImage].alt}</h3>
              <p className="text-gray-300 text-sm">{filteredImages[selectedImage].category}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Photo Credits */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 
            className="text-3xl font-bold bebas-neune gradient-text mb-6"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Behind the Lens
          </motion.h2>
          <motion.p 
            className="text-gray-300 mb-8"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            These moments were captured by our amazing photographers who somehow manage to make us look cooler than we actually are. Special thanks to everyone who's documented our journey from garage band to... slightly more professional garage band.
          </motion.p>
          <motion.div 
            className="grid md:grid-cols-3 gap-6 text-sm text-gray-400"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <div>
              <h4 className="font-semibold text-white mb-2">Live Shows</h4>
              <p>Concert photography by various talented photographers at venues across Canada</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">Studio Sessions</h4>
              <p>Behind-the-scenes shots from recording Super Deluxe and other sessions</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-2">Band Photos</h4>
              <p>Professional and not-so-professional portraits of the Supreme crew</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Gallery;